import React, { useState, useEffect } from "react";
import { Modal, Stack, Typography, Input, FormControl, FormLabel, Box } from "@mui/joy";
import PositionSelector from "./PositionSelector";
import EmployerPositionSelector from "./EmployerPositionSelector";
import ModalActions from "./ModalActions";
import ModalSheet from "../../common/ModalSheet";
import ZipCodeField from "./ZipCodeField";
import ModalHeader from "./ModalHeader";
import DateField from "./DateField";
import BooleanToggle from "./BooleanToggle";

interface FiltersModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: {
    lastActiveStartUtc: string;
    lastActiveEndUtc: string;
    zipCode: string;
    positions: string[];
    submittedToEmployerPositionIds: string[];
    verifiedPhone: boolean | null;
    hasVideo: boolean | null;
    hasResume: boolean | null;
    superApplied: boolean | null;
    minRating: number | null;
    maxRating: number | null;
    isActive: boolean | null;
    isPublished: boolean | null;
    isGloballyVisible: boolean | null;
    fullNameLike: string | null;
  }) => void;
  currentFilters?: {
    lastActiveStartUtc: string;
    lastActiveEndUtc: string;
    zipCode: string;
    positions: string[];
    submittedToEmployerPositionIds: string[];
    verifiedPhone: boolean | null;
    hasVideo: boolean | null;
    hasResume: boolean | null;
    superApplied: boolean | null;
    minRating: number | null;
    maxRating: number | null;
    isActive: boolean | null;
    isPublished: boolean | null;
    isGloballyVisible: boolean | null;
    fullNameLike: string | null;
  };
}

const ApplicantFiltersModal: React.FC<FiltersModalProps> = ({
  open,
  onClose,
  onApply,
  currentFilters,
}) => {
  const [zipCode, setZipCode] = useState("");
  const [positions, setPositions] = useState<string[]>([]);
  const [submittedToEmployerPositionIds, setSubmittedToEmployerPositionIds] = useState<string[]>([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [rangeStartUtc, setRangeStartUtc] = useState<string>("");
  const [rangeEndUtc, setRangeEndUtc] = useState<string>("");
  
  // New attribute filters
  const [verifiedPhone, setVerifiedPhone] = useState<boolean | null>(null);
  const [hasVideo, setHasVideo] = useState<boolean | null>(null);
  const [hasResume, setHasResume] = useState<boolean | null>(null);
  const [superApplied, setSuperApplied] = useState<boolean | null>(null);
  const [minRating, setMinRating] = useState<number | null>(null);
  const [maxRating, setMaxRating] = useState<number | null>(null);
  const [isActive, setIsActive] = useState<boolean | null>(null);
  const [isPublished, setIsPublished] = useState<boolean | null>(null);
  const [isGloballyVisible, setIsGloballyVisible] = useState<boolean | null>(null);
  const [fullNameLike, setFullNameLike] = useState<string | null>(null);

  // Initialize form state with current filters when modal opens
  useEffect(() => {
    if (open && currentFilters) {
      setZipCode(currentFilters.zipCode || "");
      setPositions(currentFilters.positions || []);
      setSubmittedToEmployerPositionIds(currentFilters.submittedToEmployerPositionIds || []);
      setRangeStartUtc(currentFilters.lastActiveStartUtc || "");
      setRangeEndUtc(currentFilters.lastActiveEndUtc || "");
      
      // Set the date fields with the UTC values - DateField component expects UTC ISO strings
      // and will handle the conversion to Pacific time for display
      setStartDate(currentFilters.lastActiveStartUtc || "");
      setEndDate(currentFilters.lastActiveEndUtc || "");
      
      setVerifiedPhone(currentFilters.verifiedPhone);
      setHasVideo(currentFilters.hasVideo);
      setHasResume(currentFilters.hasResume);
      setSuperApplied(currentFilters.superApplied);
      setMinRating(currentFilters.minRating);
      setMaxRating(currentFilters.maxRating);
      setIsActive(currentFilters.isActive);
      setIsPublished(currentFilters.isPublished);
      setIsGloballyVisible(currentFilters.isGloballyVisible);
      setFullNameLike(currentFilters.fullNameLike || null);
    }
  }, [open, currentFilters]);

  // Handlers for start and end date changes
  const handleStartDateChange = (value: string) => {
    setStartDate(value);
    // rangeStartUtc will be set by the setDayStartUtc prop
  };

  const handleEndDateChange = (value: string) => {
    setEndDate(value);
    // rangeEndUtc will be set by the setDayEndUtc prop
  };

  const handleApply = () => {
    onApply({
      lastActiveStartUtc: rangeStartUtc,
      lastActiveEndUtc: rangeEndUtc,
      zipCode,
      positions,
      submittedToEmployerPositionIds,
      verifiedPhone,
      hasVideo,
      hasResume,
      superApplied,
      minRating,
      maxRating,
      isActive,
      isPublished,
      isGloballyVisible,
      fullNameLike,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "none",
          },
        },
      }}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: ["flex-end", "center"],
        p: 0,
        border: "none",
      }}
    >
      <ModalSheet
        sx={{
          height: { xs: "80vh", md: "auto" },
          maxHeight: { xs: "80vh", md: "90vh" },
          scrollbarWidth: "none",
          paddingInline: "6px",
          paddingBottom: "24px",
        }}
      >
        <Stack direction="column" alignItems="center" spacing={1}>
          <ModalHeader onClose={onClose} title="Filters" />

          <Stack spacing={2} sx={{ width: "100%", px: 2 }}>
            {/* Full Name Search Filter */}
            <FormControl>
              <FormLabel
                sx={{
                  fontSize: { xs: "16px", lg: "18px" },
                  fontWeight: 500,
                  color: "#1A1B1D",
                  mb: { xs: "8px", lg: "12px" },
                }}
              >
                Applicant Name
              </FormLabel>
              <Input
                placeholder="Search by name..."
                value={fullNameLike || ''}
                onChange={(e) => setFullNameLike(e.target.value || null)}
                variant="plain"
                sx={{
                  height: "52px",
                  width: "100%",
                  borderRadius: "12px",
                  bgcolor: "#edeff3",
                  border: "none",
                  outline: "none",
                  fontSize: "16px",
                }}
              />
            </FormControl>

            {/* Last Known Active At Range Filter */}
            <FormControl>
              <FormLabel
                sx={{
                  fontSize: { xs: "16px", lg: "18px" },
                  fontWeight: 500,
                  color: "#1A1B1D",
                  mb: { xs: "8px", lg: "12px" },
                }}
              >
                Last Known Active At Range
              </FormLabel>
              <Stack direction="row" spacing={1}>
                <DateField
                  value={startDate}
                  onChange={handleStartDateChange}
                  setDayStartUtc={setRangeStartUtc}
                  placeholder="Start Date"
                />
                <Typography level="body-sm" sx={{ alignSelf: 'center' }}>to</Typography>
                <DateField
                  value={endDate}
                  onChange={handleEndDateChange}
                  setDayEndUtc={setRangeEndUtc}
                  placeholder="End Date"
                />
              </Stack>
            </FormControl>

            <ZipCodeField
              value={zipCode}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setZipCode(e.target.value)
              }
            />

            <PositionSelector
              selectedPositions={positions}
              onSelectPosition={setPositions}
            />

            <EmployerPositionSelector
              selectedPositions={submittedToEmployerPositionIds}
              onSelectPosition={setSubmittedToEmployerPositionIds}
            />

            {/* Rating Range Filter */}
            <FormControl>
              <FormLabel
                sx={{
                  fontSize: { xs: "16px", lg: "18px" },
                  fontWeight: 500,
                  color: "#1A1B1D",
                  mb: { xs: "8px", lg: "12px" },
                }}
              >
                Rating Range
              </FormLabel>
              <Stack direction="row" spacing={1} alignItems="center">
                <Input
                  type="number"
                  placeholder="Min"
                  value={minRating || ''}
                  onChange={(e) => setMinRating(e.target.value ? Number(e.target.value) : null)}
                  variant="plain"
                  slotProps={{
                    input: {
                      min: 0,
                      max: 5,
                      step: 0.1
                    }
                  }}
                  sx={{
                    flex: 1,
                    height: "52px",
                    borderRadius: "12px",
                    bgcolor: "#edeff3",
                    border: "none",
                    outline: "none",
                    fontSize: "16px",
                  }}
                />
                <Typography level="body-sm">to</Typography>
                <Input
                  type="number"
                  placeholder="Max"
                  value={maxRating || ''}
                  onChange={(e) => setMaxRating(e.target.value ? Number(e.target.value) : null)}
                  variant="plain"
                  slotProps={{
                    input: {
                      min: 0,
                      max: 5,
                      step: 0.1
                    }
                  }}
                  sx={{
                    flex: 1,
                    height: "52px",
                    borderRadius: "12px",
                    bgcolor: "#edeff3",
                    border: "none",
                    outline: "none",
                    fontSize: "16px",
                  }}
                />
              </Stack>
            </FormControl>

            {/* Two-column grid for remaining filters */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
              }}
            >
              {/* Verified Phone Filter */}
              <FormControl>
                <FormLabel
                  sx={{
                    fontSize: { xs: "16px", lg: "18px" },
                    fontWeight: 500,
                    color: "#1A1B1D",
                    mb: { xs: "8px", lg: "12px" },
                  }}
                >
                  Verified Phone
                </FormLabel>
                <BooleanToggle value={verifiedPhone} onChange={setVerifiedPhone} />
              </FormControl>

              {/* Has Video Filter */}
              <FormControl>
                <FormLabel
                  sx={{
                    fontSize: { xs: "16px", lg: "18px" },
                    fontWeight: 500,
                    color: "#1A1B1D",
                    mb: { xs: "8px", lg: "12px" },
                  }}
                >
                  Has Video
                </FormLabel>
                <BooleanToggle value={hasVideo} onChange={setHasVideo} />
              </FormControl>

              {/* Has Resume Filter */}
              <FormControl>
                <FormLabel
                  sx={{
                    fontSize: { xs: "16px", lg: "18px" },
                    fontWeight: 500,
                    color: "#1A1B1D",
                    mb: { xs: "8px", lg: "12px" },
                  }}
                >
                  Has Resume
                </FormLabel>
                <BooleanToggle value={hasResume} onChange={setHasResume} />
              </FormControl>

              {/* Super Applied Filter */}
              <FormControl>
                <FormLabel
                  sx={{
                    fontSize: { xs: "16px", lg: "18px" },
                    fontWeight: 500,
                    color: "#1A1B1D",
                    mb: { xs: "8px", lg: "12px" },
                  }}
                >
                  Super Applied
                </FormLabel>
                <BooleanToggle value={superApplied} onChange={setSuperApplied} />
              </FormControl>

              {/* Active Status Filter */}
              <FormControl>
                <FormLabel
                  sx={{
                    fontSize: { xs: "16px", lg: "18px" },
                    fontWeight: 500,
                    color: "#1A1B1D",
                    mb: { xs: "8px", lg: "12px" },
                  }}
                >
                  Is Active
                </FormLabel>
                <BooleanToggle value={isActive} onChange={setIsActive} />
              </FormControl>

              {/* Is Globally Visible Filter */}
              <FormControl>
                <FormLabel
                  sx={{
                    fontSize: { xs: "16px", lg: "18px" },
                    fontWeight: 500,
                    color: "#1A1B1D",
                    mb: { xs: "8px", lg: "12px" },
                  }}
                >
                  Globally Visible
                </FormLabel>
                <BooleanToggle value={isGloballyVisible} onChange={setIsGloballyVisible} />
              </FormControl>

              {/* Is Published Filter */}
              <FormControl>
                <FormLabel
                  sx={{
                    fontSize: { xs: "16px", lg: "18px" },
                    fontWeight: 500,
                    color: "#1A1B1D",
                    mb: { xs: "8px", lg: "12px" },
                  }}
                >
                  Published
                </FormLabel>
                <BooleanToggle value={isPublished} onChange={setIsPublished} />
              </FormControl>
            </Box>

            <ModalActions onCancel={onClose} onApply={handleApply} />
          </Stack>
        </Stack>
      </ModalSheet>
    </Modal>
  );
};

export default ApplicantFiltersModal;
